// Pure helpers for the screener stock card / table. No React here so they stay unit-testable.
//
// Gold phase 2b: the backend sends every figure computed (days to earnings,
// earnings inside the best contract, the liquidity facts against the rule the
// server runs, the setup evidence, the caps by dimension). These helpers only
// pick words and format; there is no date maths, no threshold of the liquidity
// rule and no formula here (policy P5).

import { NORMAL_CC_WINDOW_DAYS } from './earnings';
import { fmtPlainDate, fmtMonthYear } from './plainDate';
import type { StockScreenerData } from '../components/screener/columnDefinitions';

export type CardFlagKind = 'earnings' | 'spread' | 'thin-options' | 'low-volume' | 'losing-money';

export interface CardFlag {
  kind: CardFlagKind;
  /** Short chip label, e.g. "Earnings Nov 4 · inside the call" */
  chip: string;
  /** Full plain-English sentence, shown when the chip is tapped */
  detail: string;
  /** 'risk' = red, 'watch' = amber */
  level: 'risk' | 'watch';
}

type FlagInput = Pick<StockScreenerData,
  'next_earnings_date' | 'days_to_earnings' | 'earnings_inside' | 'best_dte' |
  'liquidity' | 'net_margin' | 'is_fund'>;

/**
 * Risk flags for the card: shown only when they apply, each once, as compact chips.
 * - earnings: inside the best contract (risk; the backend's one test) or within the
 *   45-day covered-call window (watch)
 * - spread / thin options / low volume: the backend's liquidity reasons against the
 *   active rule; an unknown number is flagged, never treated as a pass
 * - losing-money: negative net margin (blank, so never flagged, for funds)
 */
export function cardFlags(s: FlagInput, isCsp = false): CardFlag[] {
  const flags: CardFlag[] = [];
  const contract = isCsp ? 'put' : 'call';

  const days = s.days_to_earnings;
  const date = fmtPlainDate(s.next_earnings_date);
  if (date && days != null && days >= 0) {
    if (s.earnings_inside) {
      const dte = s.best_dte;
      flags.push({
        kind: 'earnings',
        chip: `Earnings ${date} · inside the ${contract}`,
        detail: `Earnings on ${date} fall on or before the expiry of the ${dte != null ? `${dte}-day ` : ''}${contract} behind the premium figure. The stock can gap on the report and option prices usually drop right after it.`,
        level: 'risk',
      });
    } else if (days <= NORMAL_CC_WINDOW_DAYS) {
      flags.push({
        kind: 'earnings',
        chip: `Earnings ${date} · ${days}d`,
        detail: `Earnings on ${date}, in ${days} days. A longer-dated ${contract} would span the report.`,
        level: 'watch',
      });
    }
  }

  const liq = s.liquidity;
  if (liq) {
    const r = new Set(liq.reasons);
    const rule = liq.rule;
    if (r.has('spread_above_max') && liq.spread_pct != null) {
      const pct = Math.round(liq.spread_pct);
      flags.push({
        kind: 'spread',
        chip: `Spread ${pct}%`,
        detail: `Option spreads on this stock are about ${pct}% wide, above the ${rule.max_spread_pct}% liquidity rule. Selling at the bid gives up about half of that spread against the mid price.`,
        level: 'watch',
      });
    } else if (r.has('spread_unknown')) {
      const none = r.has('oi_unknown');
      // Liquidity v2 temporary rule (LIQUIDITY_V2_UNQUOTED_FAILS off): a stock whose
      // standard contracts are not quoted yet still passes; only say so, never invent a spread.
      const notQuotedYet = !none && liq.pass;
      flags.push({
        kind: 'spread',
        chip: none ? 'No standard contract' : notQuotedYet ? 'Spread not quoted yet' : 'Spread unknown',
        detail: none
          ? `No ${contract} at delta 0.15–0.30, 30–45 days out (14–60 if none), with open interest of at least 100, so the spread and open interest are unknown and the stock is outside the liquidity rule.`
          : notQuotedYet
            ? `None of this stock's standard ${contract}s has a quoted bid and ask yet, so its spread is unknown. While quotes fill in, a missing quote does not count against the liquidity rule.`
            : `None of this stock's standard ${contract}s has a quoted bid and ask yet, so its spread is unknown. Unknown never meets the liquidity rule.`,
        level: 'watch',
      });
    }
    if (r.has('oi_below_min') && liq.max_oi != null) {
      const oi = liq.max_oi;
      flags.push({
        kind: 'thin-options',
        chip: `Thin options · ${oi.toLocaleString('en-US')} OI`,
        detail: `The most-held standard ${contract} on this stock has ${oi} open ${oi === 1 ? 'contract' : 'contracts'}, below the liquidity rule of ${rule.min_open_interest}. Fills can be slow and far from the mid price.`,
        level: 'watch',
      });
    }
    if (r.has('volume_below_min') || r.has('volume_unknown')) {
      flags.push({
        kind: 'low-volume',
        chip: r.has('volume_unknown') ? 'Volume unknown' : 'Low share volume',
        detail: r.has('volume_unknown')
          ? 'The stock\'s average daily share volume is unknown, so it is outside the liquidity rule.'
          : `The stock trades fewer than ${rule.min_stock_volume.toLocaleString('en-US')} shares a day on average, below the liquidity rule.`,
        level: 'watch',
      });
    }
  }

  if (!s.is_fund && s.net_margin != null && s.net_margin < 0) {
    const m = Math.round(s.net_margin);
    flags.push({
      kind: 'losing-money',
      chip: 'Unprofitable',
      detail: `Net margin ${m}% over the last 12 months: the company lost about $${Math.abs(m)} for every $100 of sales. Unprofitable companies tend to gap harder on bad news, and you hold the shares.`,
      level: 'watch',
    });
  }

  return flags;
}

/** The glossary's one set of words for how often a setup happens. */
export const FREQUENCY_LABEL: Record<'frequent' | 'occasional' | 'rare', string> = {
  frequent: 'Frequent',
  occasional: 'Occasional',
  rare: 'Rare',
};

/** "IV rank", or "IV rank (since Oct 2025)" while the window is under a year (interim). */
export function ivRankLabel(s: Pick<StockScreenerData, 'iv_rank_interim' | 'iv_rank_since'>): string {
  if (s.iv_rank_interim && s.iv_rank_since) {
    return `IV rank (since ${fmtMonthYear(s.iv_rank_since)})`;
  }
  return 'IV rank';
}

/** "$4.9T" / "$377.8B" / "$812.0M"; "—" when unknown. */
export function fmtMarketCap(v: number | null | undefined): string | null {
  if (v == null || !(v > 0)) return null;
  if (v >= 1e12) return `$${(v / 1e12).toFixed(1)}T`;
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  return `$${Math.round(v).toLocaleString('en-US')}`;
}

/** Plain-English range state from the volatility-contraction index (VCI). */
export function rangeWord(vci?: number | null): string | null {
  if (vci == null) return null;
  if (vci > 0.107) return 'tightening';
  if (vci < -0.095) return 'widening';
  return 'steady';
}

export const fmtSignedPct = (v: number, digits = 1) => `${v >= 0 ? '+' : ''}${v.toFixed(digits)}%`;

/** "MED_DOWN_TIGHT" → "moderate volatility, falling trend, tight range". */
export function describeSetupCell(cell?: string | null): string | null {
  if (!cell) return null;
  const parts = cell.split('_');
  if (parts.length !== 3) return null;
  const hv: Record<string, string> = { HI: 'high volatility', MED: 'moderate volatility', LO: 'low volatility' };
  const slope: Record<string, string> = { UP: 'rising trend', DOWN: 'falling trend', FLAT: 'flat trend' };
  const consol: Record<string, string> = { TIGHT: 'tight range', NORMAL: 'normal range', EXPANDING: 'expanding range' };
  return [hv[parts[0]], slope[parts[1]], consol[parts[2]]].filter(Boolean).join(', ') || null;
}

/**
 * Setup factor cut-offs. Mirrors backend app/frameworks/cluster_setup/constants.py
 * (frozen universe terciles, Apr 2026) — change both together. A stock's setup is
 * the band each of the three values falls in.
 */
export const SETUP_CUTS = {
  hv20: [26.9306, 43.5916] as [number, number],      // 20-day volatility, %
  slope12w: [-0.0542, 0.0913] as [number, number],   // 12-week trend, fraction
  consol: [-0.095, 0.107] as [number, number],       // range tightness (z), higher = tighter
};
